import { Chip, TextField } from '@material-ui/core'
import {
    Autocomplete,
    AutocompleteGetTagProps,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState
} from '@material-ui/lab'
import React, { ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import { ComboBox as ComboBoxLanguage } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../Contexts'
import Checkbox from '../Checkbox'
import Label from '../Label/Label'
import Loader from '../Loader'
import { ComboBoxItem } from '../Selects/Types'

type Props<T> = {
    label: string
    value: T | T[]
    options: ComboBoxItem<T>[]
    handleChange: (newValue?: T | T[]) => void
    multiple?: boolean
    loading?: boolean
    disabled?: boolean
    disableClearing?: boolean
}

type GeneralComboItems<T> = ComboBoxItem<T> | ComboBoxItem<T>[]

/**
 * This is a standard combo box (This should only be used for non ../../../Vendor/../../../../Vendor/../../../Vendor/react-hook-form/dist/index.ie11'
 */
export const ComboBoxAlt = <T extends unknown>({
    label,
    value,
    options,
    multiple,
    loading,
    disabled,
    handleChange,
    disableClearing
}: Props<T>): ReactElement => {
    if (options.length > 0) {
        const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
        language.loadBundle('ComboBox')

        // Standard default value.
        const [defaultValue]: ReactState<ComboBoxItem<T>> = useState<ComboBoxItem<T>>(options[0])
        // Active value of combobox.
        const [val, setVal]: ReactState<GeneralComboItems<T>> = useState<GeneralComboItems<T>>(
            multiple ? [defaultValue] : defaultValue
        )

        // DefaultedValue depends on multiple flag.
        const defaultedValue: ComboBoxItem<T> | ComboBoxItem<T>[] = multiple ? [defaultValue] : defaultValue

        const handleOnChange = (
            event: React.ChangeEvent<{ selected?: Nullable<GeneralComboItems<T>> }>,
            option: Nullable<ComboBoxItem<T>> | Nullable<ComboBoxItem<T>[]>
        ): void => {
            if (Array.isArray(option)) {
                let selectedOptionsValues: T[] = Array.from(option, (op: ComboBoxItem<T>) => op.value)
                let newOptions = option
                const isEmpty = selectedOptionsValues.length === 0

                // Unselect default option from list if there is more than 1 option.
                if (!isEmpty && selectedOptionsValues.length > 1) {
                    const defaultIndex = selectedOptionsValues.findIndex((op: T) => op === defaultValue.value)
                    if (defaultIndex !== -1) {
                        // Check default option is in default first place in array.
                        if (defaultIndex === 0) {
                            selectedOptionsValues.splice(defaultIndex, 1)
                            newOptions.splice(defaultIndex, 1)
                        } else {
                            // If the default option isn't in the first option it means it was chosen.
                            selectedOptionsValues = selectedOptionsValues.splice(defaultIndex, 1)
                            newOptions = newOptions.splice(defaultIndex, 1)
                        }
                    }
                }

                // Check to see if the array is empty if so default the option.
                handleChange(isEmpty ? [defaultValue.value] : selectedOptionsValues)
                setVal(option.length !== 0 ? newOptions : defaultedValue)
            } else {
                handleChange(option?.value ?? defaultValue.value)
                setVal(option ?? defaultedValue)
            }
        }

        useEffect(() => {
            if (Array.isArray(val)) {
                if (
                    Array.isArray(value) &&
                    val.some((v: ComboBoxItem<T>, index: number): boolean => value[index] !== v.value)
                ) {
                    // eslint-disable-next-line
                    const selectedOptions = options.filter((option: ComboBoxItem<T>): boolean =>
                        value.includes(option.value)
                    )
                    setVal(selectedOptions && selectedOptions.length > 0 ? selectedOptions : defaultedValue)
                }
            } else if (val.value !== value) {
                setVal(options.find((option: ComboBoxItem<T>): boolean => option.value === value) ?? defaultedValue)
            }
        }, [JSON.stringify(value), JSON.stringify(options)])

        useEffect(() => {
            if (Array.isArray(value)) {
                setVal(
                    options.filter((option: ComboBoxItem<T>): boolean => value.includes(option.value)) ?? defaultedValue
                )
            } else {
                setVal(options.find((option: ComboBoxItem<T>): boolean => option.value === value) ?? defaultedValue)
            }
        }, [JSON.stringify(options)])

        const onblurEvent = (): void => {
            if (Array.isArray(val) && val.length === 0) {
                setVal(defaultedValue)
            }
        }

        const getIsSelected = (option: ComboBoxItem<T>, selected: ComboBoxItem<T>): boolean =>
            (option.value === defaultValue?.value &&
                !options.some((opt: ComboBoxItem<T>): boolean => opt.value === selected.value)) ||
            option.value === selected.value

        return (
            <Autocomplete
                className='combo-box-container'
                onChange={handleOnChange}
                onBlur={onblurEvent}
                value={val}
                loading={loading}
                loadingText={language.get(ComboBoxLanguage.LoadingLabel)}
                options={options}
                multiple={multiple}
                disableCloseOnSelect={multiple}
                getOptionLabel={(option: ComboBoxItem<T>): string => option.label}
                getOptionSelected={getIsSelected}
                disableClearable={disableClearing}
                renderTags={(op: ComboBoxItem<T>[], getTagProps: AutocompleteGetTagProps): ReactNode =>
                    op.map((option, index) => (
                        <Chip
                            key={option.label}
                            label={option.label}
                            title={option.label}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderOption={(op: ComboBoxItem<T>, { selected }: AutocompleteRenderOptionState): ReactNode => (
                    <div className='combo-option'>
                        {multiple && <Checkbox isChecked={selected} />}
                        {op.label}
                    </div>
                )}
                renderInput={(params: AutocompleteRenderInputParams): ReactNode => {
                    if (loading) {
                        params.InputProps.endAdornment = (
                            <div className='combo-box-load-container'>
                                {params.InputProps.endAdornment}
                                <Loader size={16} />
                            </div>
                        )
                    }

                    return (
                        <div
                            className={`text-input combo-box ${multiple ? 'multi-cb' : ''} ${
                                disabled ? 'disabled' : ''
                            }`}
                        >
                            <Label customClass='label-left'>{label}</Label>
                            <div className='input'>
                                <TextField {...params} />
                            </div>
                        </div>
                    )
                }}
            />
        )
    }
    return <></>
}
